import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { AuctionHallService } from 'src/app/services/auction-hall/auction-hall.service';
import { AuctionHallModalService } from 'src/app/services/auction-hall-modal/auction-hall-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-auction-hall',
  templateUrl: './add-edit-auction-hall.component.html',
  styleUrls: ['./add-edit-auction-hall.component.scss']
})
export class AddEditAuctionHallComponent extends DestructorComponent implements OnInit {

  @Output() auctionHallAdded = new EventEmitter<void>();
  @Output() auctionHallUpdated = new EventEmitter<void>();

  auctionHallForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedAuctionHall: any = null;

  constructor(
    private fb: FormBuilder,
    private _auctionHallService: AuctionHallService,
    private _auctionHallModalService: AuctionHallModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.auctionHallForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_auction_hall_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceAuctionHall = this._auctionHallModalService.getSelectedAuctionHall();
        if (serviceAuctionHall) {
          this.isEditMode = true;
          this.selectedAuctionHall = serviceAuctionHall;
          this.populateForm(this.selectedAuctionHall);
        } else {
          this.isEditMode = false;
          this.selectedAuctionHall = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._auctionHallModalService.clearSelectedAuctionHall();
        this.resetForm();
      });
    }
  }

  private populateForm(auctionHall: any): void {
    this.auctionHallForm.patchValue({
      name: auctionHall.name || ''
    });
  }

  private resetForm(): void {
    this.auctionHallForm.reset();
    this.isLoading = false;
  }

  // Getter for easy access to form controls
  get name() { return this.auctionHallForm.get('name'); }

  onSubmit(): void {
    if (this.auctionHallForm.invalid) {
      this.markFormGroupTouched(this.auctionHallForm);
      return;
    }

    this.isLoading = true;

    const formData = {
      name: this.auctionHallForm.get('name')?.value
    };

    if (this.isEditMode) {
      this.updateAuctionHall(formData);
    } else {
      this.addAuctionHall(formData);
    }
  }

  private addAuctionHall(formData: any): void {
    this.subscriptions.push(
      this._auctionHallService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Auction hall added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.auctionHallAdded.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add auction hall.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error adding auction hall:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding the auction hall.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateAuctionHall(formData: any): void {
    const auctionHallId = this.selectedAuctionHall._id || this.selectedAuctionHall.id;
    this.subscriptions.push(
      this._auctionHallService.UpdateData(auctionHallId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Auction hall updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.auctionHallUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update auction hall.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error updating auction hall:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while updating the auction hall.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_auction_hall_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
